/**
 * @fileoverview Visual workflow builder — interactive graph view (MOR-736)
 *
 * React-Flow rendering of a workflow's task graph, laid out as a timeline:
 * columns are anchor-relative days (each task's `dueOffsetDays`, inferred
 * from its blockers when unset), so the practitioner can literally see the
 * schedule left-to-right. Red edges are blocking dependencies
 * (`blockedByKeys`); violet dashed edges are the subtask hierarchy
 * (`parentLocalKey`). Nodes show the firm status badge (real colour),
 * priority, assignee, a red "blocked" accent when the task waits on
 * others, and a ⚡ chip when an automation rule in this workflow targets
 * the task.
 *
 * Interactions:
 *  - click a node to open the full task editor (same card as the list view);
 *  - drag from a node's right handle to another node's left handle to add
 *    a blocking dependency (canEdit);
 *  - select a dependency edge and press Delete/Backspace to remove it;
 *  - toggle subtasks on/off; expand to full screen.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Maximize2, Minimize2, OctagonAlert, Zap } from "lucide-react";
import { toast } from "sonner";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button, Label, Switch } from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { getDisplayName } from "@/ui/composed";
import { priorityMeta } from "@/calendar/task-priority";
import { cn } from "@/utils/misc";
import { rulesTargetingTaskTitle } from "@/components/settings/automations/automation-config";
import {
  WorkflowItemCard,
  type DraftItem,
  type TaskAutomationRule,
} from "./WorkflowItemCard";
import { offsetBadgeText } from "./offsetLabels";
import { roleIconComponent } from "./role-icons";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StatusOption {
  key: string;
  name: string;
  color?: string;
}

type FlowNodeData = {
  item: DraftItem;
  status?: StatusOption;
  assigneeName?: string;
  /** Set only when the assignee is a workflow role that has an icon. */
  assigneeRoleIcon?: WorkflowRoleIcon;
  isSubtask: boolean;
  isBlocked: boolean;
  /** Workflow automation rules that target this task. */
  automationRules: TaskAutomationRule[];
};

/** taskColor token → hex, for the minimap node dots. */
const COLOR_HEX: Record<string, string> = {
  gray: "#6b7280",
  blue: "#3b82f6",
  sky: "#0ea5e9",
  teal: "#14b8a6",
  green: "#22c55e",
  emerald: "#10b981",
  amber: "#f59e0b",
  orange: "#f97316",
  red: "#ef4444",
  pink: "#ec4899",
  purple: "#a855f7",
  violet: "#8b5cf6",
  indigo: "#6366f1",
};

const BLOCKING_EDGE_COLOR = "#ef4444"; // red — a hard "can't start yet" line
const SUBTASK_EDGE_COLOR = "#8b5cf6"; // violet — structural parent/child line

// ---------------------------------------------------------------------------
// Timeline layout — columns are anchor-relative days
// ---------------------------------------------------------------------------

const NODE_WIDTH = 250;
const COLUMN_GAP = 110;
const ROW_GAP = 26;
const NODE_HEIGHT = 118;
const LABEL_ROW_HEIGHT = 56;

/**
 * Effective day per item: its `dueOffsetDays` when set, otherwise inferred
 * as the latest of its blockers'/parent's days (so undated tasks still sit
 * after the work they wait on). Cycle-safe.
 */
function computeDays(items: ReadonlyArray<DraftItem>): Map<string, number> {
  const byKey = new Map(items.map((it) => [it.localKey, it]));
  const days = new Map<string, number>();
  const visiting = new Set<string>();

  const dayOf = (key: string): number => {
    const cached = days.get(key);
    if (cached !== undefined) return cached;
    if (visiting.has(key)) return 0;
    visiting.add(key);
    const item = byKey.get(key);
    const day =
      item?.dueOffsetDays != null
        ? item.dueOffsetDays
        : Math.max(
            0,
            ...(item?.blockedByKeys ?? [])
              .filter((dep) => byKey.has(dep))
              .map((dep) => dayOf(dep)),
            item?.parentLocalKey && byKey.has(item.parentLocalKey)
              ? dayOf(item.parentLocalKey)
              : 0,
          );
    visiting.delete(key);
    days.set(key, day);
    return day;
  };

  for (const it of items) dayOf(it.localKey);
  return days;
}

// ---------------------------------------------------------------------------
// Node renderers
// ---------------------------------------------------------------------------

function WorkflowTaskNode({ data, selected }: NodeProps) {
  const {
    item,
    status,
    assigneeName,
    assigneeRoleIcon,
    isSubtask,
    isBlocked,
    automationRules,
  } = data as FlowNodeData;
  const prio = priorityMeta(item.priority);
  const PrioIcon = prio.icon;
  const RoleIcon = roleIconComponent(assigneeRoleIcon);

  return (
    <div
      className={cn(
        "flex h-[118px] w-[250px] flex-col justify-between overflow-hidden rounded-lg border bg-background px-3 py-2.5 shadow-sm transition-shadow",
        isBlocked ? "border-red-400 dark:border-red-700" : "border-border",
        selected && "ring-2 ring-primary",
        isSubtask && "border-dashed",
      )}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!size-2.5 !border-border !bg-muted-foreground/60"
      />
      <div className="flex items-start justify-between gap-1.5">
        <p
          className="truncate text-sm font-medium text-foreground"
          title={item.title}
        >
          {item.title || "Untitled task"}
        </p>
        {isBlocked && (
          <OctagonAlert
            className="mt-0.5 size-3.5 shrink-0 text-red-500"
            aria-label="Blocked by other tasks"
          />
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        {status && (
          <Badge
            color={(status.color as BadgeColor) ?? "gray"}
            variant="soft"
            size="sm"
          >
            {status.name}
          </Badge>
        )}
        {item.priority !== null && item.priority !== 0 && (
          <Badge color={prio.color} variant="soft" size="sm" className="gap-1">
            <PrioIcon className="size-3" />
            {prio.label}
          </Badge>
        )}
        {(item.dueOffsetDays !== null ||
          item.dueOffsetUnit === "quarter_end") && (
          <Badge variant="outline" size="sm" className="tabular-nums">
            {offsetBadgeText(item.dueOffsetDays ?? 0, item.dueOffsetUnit)}
          </Badge>
        )}
        {isSubtask && (
          <Badge
            variant="outline"
            size="sm"
            className="border-violet-300 text-violet-600 dark:border-violet-800 dark:text-violet-400"
          >
            Subtask
          </Badge>
        )}
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-1.5">
        <span className="inline-flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground">
          {RoleIcon && <RoleIcon className="size-3 shrink-0" />}
          <span className="truncate">{assigneeName ?? "Unassigned"}</span>
        </span>
        {automationRules.length > 0 && (
          <span
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
            title={`Automated by: ${automationRules.map((r) => r.name).join(", ")}`}
          >
            <Zap className="size-3" />
            {automationRules.length}
          </span>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        className="!size-2.5 !border-border !bg-muted-foreground/60"
      />
    </div>
  );
}

/** Non-interactive "Day N" column header. */
function DayLabelNode({ data }: NodeProps) {
  return (
    <div className="w-[250px] pb-2 text-center">
      <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {(data as { label: string }).label}
      </span>
    </div>
  );
}

const NODE_TYPES = { workflowTask: WorkflowTaskNode, dayLabel: DayLabelNode };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WorkflowFlowView({
  orgId,
  templateId,
  templateName,
  items,
  statuses,
  labels,
  canEdit,
  onPatchItem,
  onRemoveItem,
}: {
  orgId: Id<"organizations">;
  templateId: Id<"taskTemplates">;
  /** Workflow name — passed to the task card's quick rule builder. */
  templateName: string;
  items: ReadonlyArray<DraftItem>;
  statuses: ReadonlyArray<StatusOption>;
  labels: ReadonlyArray<{ key: string; name: string }>;
  canEdit: boolean;
  /** Same patch callback the list view uses — edits write through it. */
  onPatchItem: (localKey: string, patch: Partial<DraftItem>) => void;
  onRemoveItem: (localKey: string) => void;
}) {
  const [showSubtasks, setShowSubtasks] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Escape leaves full screen (the dialog handles its own Escape first).
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !editingKey) setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen, editingKey]);

  const { data: members } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, { orgId }),
  );
  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members ?? []) {
      map.set(
        m.user._id,
        getDisplayName(m.user.firstName, m.user.lastName, m.user.email),
      );
    }
    return map;
  }, [members]);

  // Workflow roles (MOR-748) — so role-assigned tasks label their node with
  // the role name instead of appearing unassigned.
  const { data: workflowRoles } = useQuery(
    convexQuery(api.tasks.workflowRoles.listByOrg, { orgId }),
  );
  const roleById = useMemo(
    () => new Map((workflowRoles ?? []).map((r) => [r._id as string, r])),
    [workflowRoles],
  );

  // This workflow's automation rules — used to flag automated tasks.
  const { data: rules } = useQuery(
    convexQuery(api.automations.rules.listByTemplate, { templateId }),
  );

  const statusByKey = useMemo(
    () => new Map(statuses.map((s) => [s.key, s])),
    [statuses],
  );

  /** Rules targeting a given task title (trigger filter or action match). */
  const automationRulesForTitle = useCallback(
    (title: string): TaskAutomationRule[] =>
      rulesTargetingTaskTitle(rules ?? [], title),
    [rules],
  );

  const visibleItems = useMemo(
    () => (showSubtasks ? items : items.filter((it) => !it.parentLocalKey)),
    [items, showSubtasks],
  );

  const { nodes, edges } = useMemo(() => {
    const visibleKeys = new Set(visibleItems.map((it) => it.localKey));
    const days = computeDays(visibleItems);

    // Distinct days → timeline columns, in ascending day order.
    const uniqueDays = [
      ...new Set(visibleItems.map((it) => days.get(it.localKey) ?? 0)),
    ].sort((a, b) => a - b);
    const columnByDay = new Map(uniqueDays.map((day, i) => [day, i]));

    // Explicit width/height on every node: React Flow's minimap only draws
    // nodes whose user objects carry dimensions (controlled-flow mode never
    // writes measurements back onto our objects).
    const nodes: Node[] = uniqueDays.map((day) => ({
      id: `day:${day}`,
      type: "dayLabel",
      position: {
        x: (columnByDay.get(day) ?? 0) * (NODE_WIDTH + COLUMN_GAP),
        y: -LABEL_ROW_HEIGHT,
      },
      width: NODE_WIDTH,
      height: 36,
      data: { label: day === 0 ? "Day 0 (anchor)" : `Day ${day}` },
      draggable: false,
      selectable: false,
      connectable: false,
    }));

    const rowsPerColumn = new Map<number, number>();
    for (const item of visibleItems) {
      const day = days.get(item.localKey) ?? 0;
      const column = columnByDay.get(day) ?? 0;
      const row = rowsPerColumn.get(column) ?? 0;
      rowsPerColumn.set(column, row + 1);
      nodes.push({
        id: item.localKey,
        type: "workflowTask",
        position: {
          x: column * (NODE_WIDTH + COLUMN_GAP),
          y: row * (NODE_HEIGHT + ROW_GAP),
        },
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        data: {
          item,
          status: item.statusKey ? statusByKey.get(item.statusKey) : undefined,
          assigneeName: item.assigneeRoleId
            ? roleById.get(item.assigneeRoleId)?.name
            : item.assigneeUserId
              ? memberNameById.get(item.assigneeUserId)
              : undefined,
          assigneeRoleIcon: item.assigneeRoleId
            ? roleById.get(item.assigneeRoleId)?.icon
            : undefined,
          isSubtask: !!item.parentLocalKey,
          isBlocked: item.blockedByKeys.some((k) => visibleKeys.has(k)),
          automationRules: automationRulesForTitle(item.title),
        } satisfies FlowNodeData,
        connectable: canEdit,
        deletable: false,
      });
    }

    const edges: Edge[] = [];
    for (const item of visibleItems) {
      for (const dep of item.blockedByKeys) {
        if (!visibleKeys.has(dep)) continue;
        edges.push({
          id: `dep:${dep}->${item.localKey}`,
          source: dep,
          target: item.localKey,
          animated: true,
          deletable: canEdit,
          label: "blocks",
          labelStyle: { fontSize: 10, fill: BLOCKING_EDGE_COLOR },
          style: { stroke: BLOCKING_EDGE_COLOR, strokeWidth: 1.5 },
        });
      }
      if (
        showSubtasks &&
        item.parentLocalKey &&
        visibleKeys.has(item.parentLocalKey)
      ) {
        edges.push({
          id: `sub:${item.parentLocalKey}->${item.localKey}`,
          source: item.parentLocalKey,
          target: item.localKey,
          style: {
            stroke: SUBTASK_EDGE_COLOR,
            strokeDasharray: "5 4",
            strokeWidth: 1.5,
            opacity: 0.8,
          },
          deletable: false,
        });
      }
    }
    return { nodes, edges };
  }, [
    visibleItems,
    statusByKey,
    memberNameById,
    roleById,
    automationRulesForTitle,
    canEdit,
    showSubtasks,
  ]);

  /** Drag-to-connect adds a blocking dependency (source blocks target). */
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!canEdit || !connection.source || !connection.target) return;
      if (connection.source === connection.target) return;
      if (
        connection.source.startsWith("day:") ||
        connection.target.startsWith("day:")
      )
        return;
      const target = items.find((it) => it.localKey === connection.target);
      if (!target) return;
      if (target.blockedByKeys.includes(connection.source)) return;
      // Reject direct cycles here; the backend re-validates the full DAG.
      const source = items.find((it) => it.localKey === connection.source);
      if (source?.blockedByKeys.includes(connection.target)) {
        toast.error("These tasks already block each other the other way.");
        return;
      }
      onPatchItem(target.localKey, {
        blockedByKeys: [...target.blockedByKeys, connection.source],
      });
    },
    [canEdit, items, onPatchItem],
  );

  /** Deleting a dependency edge removes it from the blocked task. */
  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (!canEdit) return;
      for (const change of changes) {
        if (change.type !== "remove") continue;
        const match = /^dep:(.+)->(.+)$/.exec(change.id);
        if (!match) continue;
        const [, source, targetKey] = match;
        const target = items.find((it) => it.localKey === targetKey);
        if (!target) continue;
        onPatchItem(target.localKey, {
          blockedByKeys: target.blockedByKeys.filter((k) => k !== source),
        });
      }
    },
    [canEdit, items, onPatchItem],
  );

  const editingItem = editingKey
    ? items.find((it) => it.localKey === editingKey)
    : undefined;
  const editingIndex = editingKey
    ? items.findIndex((it) => it.localKey === editingKey)
    : -1;
  const otherItems = useMemo(
    () => items.map((it) => ({ localKey: it.localKey, title: it.title })),
    [items],
  );

  return (
    <div
      className={cn(
        "space-y-2",
        fullscreen && "fixed inset-0 z-50 flex flex-col bg-background p-4",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-5"
              style={{ backgroundColor: BLOCKING_EDGE_COLOR }}
            />
            blocking dependency
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-5 border-t-2 border-dashed"
              style={{ borderColor: SUBTASK_EDGE_COLOR }}
            />
            subtask
          </span>
          <span className="inline-flex items-center gap-1">
            <Zap className="size-3 text-amber-500" />
            targeted by an automation
          </span>
          <span className="inline-flex items-center gap-1">
            <OctagonAlert className="size-3 text-red-500" />
            blocked
          </span>
          <span>Click a task to edit it.</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="flow-show-subtasks" className="text-xs font-normal">
              Show subtasks
            </Label>
            <Switch
              id="flow-show-subtasks"
              checked={showSubtasks}
              onCheckedChange={setShowSubtasks}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setFullscreen((f) => !f)}
          >
            {fullscreen ? (
              <>
                <Minimize2 className="size-3.5" /> Exit full screen
              </>
            ) : (
              <>
                <Maximize2 className="size-3.5" /> Full screen
              </>
            )}
          </Button>
        </div>
      </div>
      <div
        className={cn(
          "rounded-lg border border-border bg-background",
          fullscreen ? "min-h-0 flex-1" : "h-[600px]",
        )}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onConnect={handleConnect}
          onEdgesChange={handleEdgesChange}
          onNodeClick={(_, node) => {
            if (node.type === "workflowTask") setEditingKey(node.id);
          }}
          nodesDraggable={false}
          nodesConnectable={canEdit}
          elementsSelectable
          fitView
          minZoom={0.15}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} />
          <Controls showInteractive={false} />
          <MiniMap
            pannable
            zoomable
            className="rounded-md border border-border"
            nodeColor={(node) => {
              if (node.type !== "workflowTask") return "transparent";
              const data = node.data as FlowNodeData;
              if (data.isBlocked) return COLOR_HEX.red;
              return COLOR_HEX[data.status?.color ?? "gray"] ?? COLOR_HEX.gray;
            }}
          />
        </ReactFlow>
      </div>

      {/* Click-to-edit — the same full task card as the list view, so every
          field (status, assignee, dates, parent, blocked-by) is editable. */}
      <Dialog
        open={!!editingItem}
        onOpenChange={(open) => !open && setEditingKey(null)}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem?.title || "Untitled task"}</DialogTitle>
            <DialogDescription>
              {canEdit
                ? "Changes save automatically with the rest of the workflow."
                : "Read-only — you need owner or admin access to edit."}
            </DialogDescription>
          </DialogHeader>
          {editingItem && (
            <WorkflowItemCard
              key={editingItem.localKey}
              orgId={orgId}
              item={editingItem}
              index={editingIndex}
              statuses={statuses}
              labels={labels}
              otherItems={otherItems}
              disabled={!canEdit}
              defaultExpanded
              automationRules={automationRulesForTitle(editingItem.title)}
              templateId={templateId}
              templateName={templateName}
              workflowTaskTitles={items.map((it) => it.title)}
              onChange={(patch) => onPatchItem(editingItem.localKey, patch)}
              onRemove={() => {
                onRemoveItem(editingItem.localKey);
                setEditingKey(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
